// Ren komposisjons-funksjon for kundeportaler.
// Tar en ordnet liste av seksjoner (standard + custom) + config-verdier og
// bygger én standalone HTML-streng. Delte assets (CSS/JS/fonter/bilder) hostes
// på /portal/* (Vercel-statics), så fila blir liten. Kundenavn/pris fylles inn
// runtime av /portal/app.js via window.PORTAL_CONFIG + data-bind.

const ESC = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
export const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ESC[c]);
// Tekst med linjeskift -> avsnitt/<br>
const richText = (s) =>
  String(s || "")
    .split(/\n{2,}/)
    .map((p) => `<p class="intro reveal">${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("\n");

// Faste tekster i rammen rundt seksjonene, per språk. Selve seksjonsinnholdet
// kommer fra portal_sections (html / html_en). Nav: kontakt rendres som CTA,
// hero har ingen lenke.
const PORTAL_LABELS = {
  no: {
    htmlLang: "nb", titleSuffix: "Kundeportal", customerDefault: "Kunde",
    nav: { om: "Om Royal", velkomst: "Velkomst", artister: "Artister", produksjoner: "Produksjoner", pakker: "Pakker", pris: "Pris", materiell: "Materiell" },
    navCta: "Ta kontakt", navMore: "Mer", openMenu: "Åpne meny", closeMenu: "Lukk meny", close: "Lukk",
    customEyebrow: "Skreddersydd for dere", play: "Spill av",
  },
  en: {
    htmlLang: "en", titleSuffix: "Client portal", customerDefault: "Client",
    nav: { om: "About Royal", velkomst: "Welcome", artister: "Artists", produksjoner: "Productions", pakker: "Packages", pris: "Pricing", materiell: "Material" },
    navCta: "Get in touch", navMore: "More", openMenu: "Open menu", closeMenu: "Close menu", close: "Close",
    customEyebrow: "Tailored for you", play: "Play",
  },
};

const FOOTER = `<footer class="foot">
  <div class="wrap foot-inner">
    <img class="logo" src="/portal/img/77f6b9e8-90df-46da-8a68-f70481f9ad51.png" alt="ROYAL">
    <div class="mantra">
      <span>Concept</span><i class="sep"></i>
      <span>Creation</span><i class="sep"></i>
      <span>Performance</span><i class="sep"></i>
      <span>Presence</span>
    </div>
    <a class="link" href="https://royal.show" target="_blank" rel="noopener">royal.show <span class="ext">↗</span></a>
  </div>
</footer>`;

const lightbox = (L) => `<div class="lightbox" id="lightbox">
  <button class="lightbox-close" id="lightbox-close" aria-label="${L.close}">×</button>
  <div class="lightbox-inner" id="lightbox-inner"></div>
</div>`;

// Hero-justeringer per portal: egen introtekst, og prisknappen bare når det
// faktisk finnes en pris å vise. Funksjons-replacer så tekst med «$» ikke
// tolkes som erstatningsmønster.
function tuneHero(html, { heroIntro, showPrice }) {
  let out = html;
  const intro = String(heroIntro || "").trim();
  if (intro) {
    out = out.replace(/(<p class="hero-sub reveal">)[\s\S]*?(<\/p>)/, (m, open, close) =>
      `${open}${esc(intro).replace(/\n/g, "<br>")}${close}`
    );
  }
  if (!showPrice) out = out.replace(/<a class="btn btn-primary" href="#pris">[\s\S]*?<\/a>\s*/, "");
  return out;
}

// Render én strukturert custom-seksjon
function renderCustom(item, L) {
  const id = item.id || "custom";
  const eyebrow = item.eyebrow ? esc(item.eyebrow) : L.customEyebrow;
  const media = [];
  if (item.video_url) {
    // Video → ett video-kort. Opplastet bilde blir thumbnail; ellers henter
    // app.js den fra Vimeo/YouTube runtime. (Ingen separat bilde-blokk i tillegg.)
    media.push(
      `<div class="video-card reveal" data-video="${esc(item.video_url)}" data-title="${esc(item.title || "Royal")}" style="margin-top:24px">
         ${item.image_url ? `<img src="${esc(item.image_url)}" alt="${esc(item.title || "")}" loading="lazy">` : ""}
         <span class="play-badge"><span class="tri"></span>${L.play}</span>
         ${item.title ? `<div class="video-meta"><span class="label">${esc(item.title)}</span></div>` : ""}
       </div>`
    );
  } else if (item.image_url) {
    media.push(
      `<div class="reveal" style="margin-top:32px"><img src="${esc(item.image_url)}" alt="${esc(item.title || "")}" style="width:100%;border-radius:18px;display:block"></div>`
    );
  }
  return `<section class="section glow" id="${esc(id)}">
    <div class="wrap">
      <div class="section-head">
        <div class="eyebrow reveal"><span class="slash">/</span><span>${eyebrow}</span></div>
        <h2 class="lead reveal">${esc(item.title || "")}</h2>
        ${richText(item.text)}
      </div>
      ${media.join("\n")}
    </div>
  </section>`;
}

// order: array of { type:"section", key } | { type:"custom", id, title, text, eyebrow, image_url, video_url }
// sectionMap: { key: { label, html } }
// config: { customer, discountPct, guidePrice, contactEmail, heroVideo, ctaVideo,
//           language ("no"|"en"), heroIntro (tom = malens standardtekst) }
export function composePortalHtml({ order = [], sectionMap = {}, config = {} }) {
  const lang = config.language === "en" ? "en" : "no";
  const L = PORTAL_LABELS[lang];
  const customer = config.customer || L.customerDefault;
  const enabled = order.filter(Boolean);
  // «Se prisene deres» i hero krever at pris-seksjonen er med og at det er lagt inn en pris.
  const showPrice =
    enabled.some((it) => it.type === "section" && it.key === "pris") && Number(config.guidePrice) > 0;

  // Nav-lenker fra rekkefølgen
  const navLinks = [];
  let hasKontakt = false;
  for (const it of enabled) {
    if (it.type === "section") {
      if (it.key === "hero") continue;
      if (it.key === "kontakt") { hasKontakt = true; continue; }
      const label = L.nav[it.key] || (sectionMap[it.key] && sectionMap[it.key].label) || it.key;
      navLinks.push(`<a href="#${esc(it.key)}">${esc(label)}</a>`);
    } else if (it.type === "custom") {
      navLinks.push(`<a href="#${esc(it.id)}">${esc(it.title || L.navMore)}</a>`);
    }
  }
  if (hasKontakt) navLinks.push(`<a class="nav-cta" href="#kontakt">${L.navCta}</a>`);

  // Samme lenkesett brukes både i den horisontale baren og i side-menyen (drawer).
  const navLinksHtml = navLinks.join("\n    ");
  const nav = `<nav class="nav" id="nav">
  <a class="nav-brand" href="#top">
    <img src="/portal/img/77f6b9e8-90df-46da-8a68-f70481f9ad51.png" alt="ROYAL">
    <span class="x">×</span>
    <span class="partner" data-bind="customer">${esc(customer)}</span>
  </a>
  <div class="nav-links">
    ${navLinksHtml}
  </div>
  <button class="nav-burger" id="nav-burger" type="button" aria-label="${L.openMenu}" aria-expanded="false" aria-controls="nav-drawer">
    <span></span><span></span><span></span>
  </button>
  <div class="nav-backdrop" id="nav-backdrop"></div>
  <aside class="nav-drawer" id="nav-drawer" aria-hidden="true">
    <button class="nav-drawer-close" id="nav-drawer-close" type="button" aria-label="${L.closeMenu}">×</button>
    <div class="nav-drawer-links">
    ${navLinksHtml}
    </div>
  </aside>
</nav>`;

  // Hoveddel
  let main = enabled
    .map((it) => {
      if (it.type === "section") {
        const sec = sectionMap[it.key];
        if (!sec) return "";
        // Engelsk versjon når den finnes, ellers norsk (samme regel som tilbud)
        let html = lang === "en" && sec.html_en && sec.html_en.trim() ? sec.html_en : sec.html;
        if (it.key === "hero") html = tuneHero(html, { heroIntro: config.heroIntro, showPrice });
        return html;
      }
      if (it.type === "custom") return renderCustom(it, L);
      return "";
    })
    .filter(Boolean)
    .join("\n\n");

  // Interne CTA-knapper kan peke på seksjoner som er skrudd av (f.eks. hero sin
  // "Se produksjonene" -> #produksjoner). Omdiriger døde anker-lenker til en
  // fornuftig fallback (kontakt hvis den finnes, ellers toppen) i stedet for ingenting.
  const presentIds = new Set(["top"]);
  enabled.forEach((it) => presentIds.add(it.type === "section" ? it.key : it.id));
  const anchorFallback = presentIds.has("kontakt") ? "#kontakt" : "#top";
  main = main.replace(/href="#([a-z0-9-]+)"/gi, (m, id) =>
    presentIds.has(id) ? m : `href="${anchorFallback}"`
  );

  // Bare runtime-relevante config-nøkler sendes til siden
  const runtimeConfig = {
    customer,
    discountPct: Number(config.discountPct) || 0,
    guidePrice: Number(config.guidePrice) || 0,
    contactEmail: config.contactEmail || "post@royaldancecompany.com",
    heroVideo: config.heroVideo || "",
    ctaVideo: config.ctaVideo || "",
    volumeDiscount: !!config.volumeDiscount,
    volumeTiers: Array.isArray(config.volumeTiers) ? config.volumeTiers : [],
    lang,
  };

  return `<!DOCTYPE html>
<html lang="${L.htmlLang}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Royal × ${esc(customer)} · ${L.titleSuffix}</title>
<link rel="stylesheet" href="/portal/styles.css">
</head>
<body>
${nav}
<main id="top">
${main}
</main>
${FOOTER}
${lightbox(L)}
<script>window.PORTAL_CONFIG = ${JSON.stringify(runtimeConfig)};</script>
<script src="/portal/app.js"></script>
</body>
</html>`;
}
